import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, PenTool, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signable pledge — for authenticated users. Records to DB, shows live count.
export default function PledgeCard() {
  const { user, login } = useAuth();
  const [signed, setSigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const stats = await fetch(`${API}/pledge/stats`).then((r) => r.json());
        setCount(stats.count ?? 0);
        if (user) {
          const me = await fetch(`${API}/pledge/me`, { credentials: "include" });
          if (me.ok) {
            const j = await me.json();
            setSigned(!!j.signed);
            setSignedAt(j.signed_at || null);
          }
        }
      } catch {
        /* ignore */
      }
    })();
  }, [user]);

  const sign = async () => {
    if (!user) return login();
    if (signed) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/pledge`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const j = await res.json();
        setSigned(true);
        setSignedAt(j.signed_at);
        setCount(j.count);
      }
    } finally {
      setBusy(false);
    }
  };

  const numberLabel =
    count === null ? "—" : count.toLocaleString("en-US");

  return (
    <div
      data-testid="pledge-card"
      className="relative overflow-hidden border border-white/10 bg-void p-8 md:p-10"
    >
      <span className="pointer-events-none absolute -right-6 -top-8 select-none font-display text-[10rem] leading-none text-white/[0.04]">
        ✕
      </span>
      <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-rebellion">
        The Citizen&rsquo;s Pledge
      </p>
      <h3 className="mb-6 font-display text-3xl uppercase leading-[0.95] tracking-tight text-paper md:text-5xl">
        Sign the <span className="text-rebellion">Rebellion.</span>
      </h3>

      <ul className="mb-8 space-y-4">
        {[
          "I will question every party that claims to speak for me.",
          "I will read the rules they hoped I would ignore.",
          "I refuse to be governed quietly.",
        ].map((l, i) => (
          <li key={i} className="flex items-start gap-4">
            <span className="mt-1 font-display text-2xl leading-none text-rebellion">
              0{i + 1}
            </span>
            <p className="font-editorial text-lg leading-relaxed text-white/80 md:text-xl">
              {l}
            </p>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-6">
        {signed ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 border border-rebellion bg-rebellion/10 px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-rebellion"
            data-testid="pledge-signed-state"
          >
            <Check className="h-4 w-4" />
            <span>Signed</span>
            {signedAt ? (
              <span className="text-rebellion/70">
                ·{" "}
                {new Date(signedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            ) : null}
          </motion.div>
        ) : (
          <button
            data-testid="pledge-sign-button"
            onClick={sign}
            disabled={busy}
            className="group inline-flex items-center gap-3 border border-rebellion bg-rebellion px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-transparent hover:text-rebellion disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PenTool className="h-4 w-4" />
            )}
            {user ? "Sign the Pledge" : "Sign In to Pledge"}
          </button>
        )}

        <div className="flex items-baseline gap-3" data-testid="pledge-count">
          <span className="font-display text-4xl leading-none text-paper md:text-5xl">
            {numberLabel}
          </span>
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-white/50">
            {count === 1 ? "citizen has signed" : "citizens have signed"}
          </span>
        </div>
      </div>
    </div>
  );
}
